// src/utils/OrderHelpers.js
import api from '../services/api';
import { productService, inventoryService, orderTrackingService } from '../services/api';
import { format } from 'date-fns';
import { startTicketPrint } from './ticketPrintDispatch';


const isInfiniteStock = (qty) => {
    return Number(qty) === -9999 || String(qty) === "-9999";
};

const deductStockForPendingOrder = async (products, selectedDiningOption, tableNumber, cashierName) => {
    try {
        for (const product of products) {
            if (product.stock_calculation_type === 'auto') {
                await api.post('/inventory/deduct-for-pending-order', {
                    product_code: product.product_code,
                    quantity: product.quantity,
                    dining_option: selectedDiningOption,
                    table_key: `${selectedDiningOption}-${tableNumber}`,
                    created_by: `Pending Order - ${cashierName}`,
                    notes: `Pending order stock deduction for ${product.name}`
                });
            } else {
                // For manual stock products, get current stock first
                const currentProducts = await productService.getAllProducts();
                const currentProduct = currentProducts.find(p => p.product_code === product.product_code);


                if (currentProduct && !isInfiniteStock(currentProduct.quantity)) {
                    console.log('=== STOCK DEDUCTION DEBUG ===');
                    console.log('Product from order:', {
                        name: product.name,
                        product_code: product.product_code,
                        quantity: product.quantity,
                        stock_calculation_type: product.stock_calculation_type
                    });
                    console.log('Current product from DB:', {
                        name: currentProduct?.name,
                        product_code: currentProduct?.product_code,
                        quantity: currentProduct?.quantity
                    });
                    console.log('Calculation:', `${currentProduct?.quantity} - ${product.quantity} = ${currentProduct?.quantity - product.quantity}`);

                    const newQuantity = Math.max(0, currentProduct.quantity - product.quantity);
                    await productService.updateProduct(product.product_code, {
                        ...currentProduct,
                        quantity: newQuantity
                    });
                } else {
                    console.log('-----------Infinite product so no stock deduction ------');

                }
            }
        }
    } catch (error) {
        console.error('Error deducting stock for pending order:', error);
        throw error;
    }
};

const revertStockDeduction = async (products, cashierName) => {
    try {
        for (const product of products) {
            if (product.stock_calculation_type === 'auto') {
                await inventoryService.deductStockForStaffAllowance({
                    product_code: product.product_code,
                    quantity: -product.quantity,
                    created_by: `Revert - ${cashierName}`,
                    notes: `Reverting stock deduction for failed order: ${product.name}`
                });
            } else {
                if (!isInfiniteStock(product.quantity)) {
                    // Fetch current stock and add back the deducted quantity
                    const currentProducts = await productService.getAllProducts();
                    const currentProduct = currentProducts.find(p => p.product_code === product.product_code);
                    if (currentProduct) {
                        const restoredQuantity = currentProduct.quantity + product.quantity;
                        await productService.updateProduct(product.product_code, {
                            ...currentProduct,
                            quantity: restoredQuantity
                        });
                    }
                }
            }
        }
    } catch (error) {
        console.error('Error reverting stock deduction:', error);
        throw error;
    }
};

const createOrderTracking = async (order, orderId, tenantConfig, selectedDiningOption, tableNumber, notes, { printableCategories: externalPC } = {}) => {
    try {
        const printableCategories = externalPC || tenantConfig?.printable_categories || [];
        console.log('=== CREATE ORDER TRACKING DEBUG ===');
        console.log('Order ID:', orderId);
        console.log('Printable categories:', printableCategories);
        console.log('Order products:', order.products);

        for (const category of printableCategories) {
            const categoryItems = order.products.filter(p => p.category === category);
            console.log(`Category ${category}: ${categoryItems.length} items`);

            if (categoryItems.length > 0) {
                const trackingData = {
                    transaction_id: null,
                    order_id: orderId,
                    category: category,
                    printed_categories: false,
                    status: "submitted",
                    items: categoryItems.map(item => ({
                        name: item.name,
                        quantity: item.quantity,
                        notes: item.notes || null,
                        add_ons: item.add_ons || []
                    })),
                    table_info: {
                        dining_option: selectedDiningOption,
                        table_number: tableNumber
                    },
                    total_items_count: categoryItems.reduce((sum, item) => sum + item.quantity, 0),
                    notes: notes || null
                };

                console.log(`Creating tracking for category ${category}:`, trackingData);

                try {
                    const response = await api.post('/orders/tracking', trackingData);
                    console.log(` Order tracking created for ${category}:`, response.data);

                    const eventData = {
                        type: 'orderSubmitted',
                        category: category,
                        orderType: 'new_order',
                        orderId: orderId,
                        timestamp: Date.now()
                    };

                    console.log(` DISPATCHING cross-tab orderSubmitted event for category: ${category}, orderId: ${orderId}`);

                    // Cross-tab event (localStorage)
                    localStorage.setItem('orderEvent', JSON.stringify(eventData));
                    localStorage.removeItem('orderEvent');

                    // Same-tab event (custom DOM event)
                    window.dispatchEvent(new CustomEvent('orderEvent', { detail: eventData }));

                    console.log(' Cross-tab event dispatched successfully');
                } catch (trackingError) {
                    console.error(` Failed to create tracking for ${category}:`, trackingError);
                    console.error('Error response:', trackingError.response?.data);
                }
            }
        }
    } catch (error) {
        console.error(' Error creating order tracking:', error);
    }
};

const updateOrderTrackingRecords = async (originalProducts, updatedProducts, orderId, originalOrder, tenantConfig, selectedDiningOption, editingOrderKey, username, products, printedCategories = false, { printableCategories: externalPC } = {}) => {
    try {
        console.log(' [OrderHelpers] updateOrderTrackingRecords called with:');
        console.log('originalProducts:', originalProducts);
        console.log('updatedProducts:', updatedProducts);
        console.log('orderId:', orderId);

        const printableCategories = externalPC || tenantConfig?.printable_categories || [];
        const newItemsToTrack = calculateNewItemsForTracking(originalProducts, updatedProducts);

        console.log(' [OrderHelpers] Calculated new items to track:', newItemsToTrack);
        console.log(' [OrderHelpers] Printable categories:', printableCategories);

        // Always mark existing tracking records as edited, even if no new items
        try {
            await orderTrackingService.markOrderEdited(orderId);
        } catch (editErr) {
            console.error(' [OrderHelpers] Failed to mark existing records as edited:', editErr);
        }

        if (newItemsToTrack.length === 0) {
            console.log(' [OrderHelpers] No new items to track - exiting early');
            return;
        }

        // Group new items by category (ALL categories, not just printable)
        const itemsByCategory = {};
        newItemsToTrack.forEach(item => {
            if (!itemsByCategory[item.category]) {
                itemsByCategory[item.category] = [];
            }
            itemsByCategory[item.category].push(item);
        });

        console.log(' [OrderHelpers] Items grouped by category:', itemsByCategory);

        const trackingPromises = [];

        // Create tracking records for ALL categories (both printable and non-printable)
        Object.entries(itemsByCategory).forEach(([category, categoryItems]) => {
            console.log(` [OrderHelpers] Creating tracking record for category: ${category} with ${categoryItems.length} items`);

            const trackingData = {
                transaction_id: null, // This is a pending order
                order_id: orderId,
                category: category,
                printed_categories: printedCategories, // Always false for waiter orders - cashier needs to print
                items: categoryItems.map(item => ({
                    name: item.name,
                    quantity: item.quantity,
                    notes: item.notes || null,
                    add_ons: item.add_ons || []
                })),
                table_info: {
                    dining_option: selectedDiningOption,
                    table_number: originalOrder.tableNumber
                },
                total_items_count: categoryItems.reduce((sum, item) => sum + item.quantity, 0),
                notes: `Staff: ${username}${originalOrder.notes ? ` | ${originalOrder.notes}` : ''}`
            };

            console.log(` [OrderHelpers] Tracking data for ${category}:`, trackingData);

            const promise = orderTrackingService.createOrderTracking(trackingData)
                .then(result => {
                    console.log(` [OrderHelpers] Created tracking record for ${category}:`, result.tracking_id);
                    // Device Printer Config Task 8 (append-mode wiring) — a KDS
                    // ticket fires alongside the tracking record, entirely
                    // best-effort (see utils/ticketPrintDispatch.js).
                    //
                    // Whole-branch review, finding 3 — the print, unlike the
                    // tracking record above, is filtered by the tenant's
                    // printable categories. Creating a record for EVERY
                    // category is deliberate and stays (the KDS must show
                    // them all, printable or not), but printing one for a
                    // category the tenant excluded made the same order
                    // behave differently depending on whether it was
                    // submitted from the waiter or the cashier surface —
                    // dispatchKitchenTickets only ever loops over
                    // printableCategories.
                    //
                    // Whole-branch review, finding 1(b) — started, never
                    // awaited: this used to sit inside the promise that
                    // `Promise.all(trackingPromises)` waits on, so a slow
                    // printer delayed markOrderEdited below and the KDS
                    // showed the order as un-edited for the whole hang.
                    if (result?.tracking_id && printableCategories.includes(category)) {
                        startTicketPrint(category, result.tracking_id, tenantConfig);
                    }
                    return result;
                })
                .catch(error => {
                    console.error(` [OrderHelpers] Failed to create tracking for ${category}:`, error);
                    throw error;
                });

            trackingPromises.push(promise);
        });

        // Wait for all tracking records to be created
        await Promise.all(trackingPromises);
        console.log(' [OrderHelpers] All tracking records created successfully');

        // Mark ALL records (including newly created ones) as edited
        try {
            await orderTrackingService.markOrderEdited(orderId);
        } catch (editErr) {
            console.error(' [OrderHelpers] Failed to mark new records as edited:', editErr);
        }

    } catch (error) {
        console.error(' [OrderHelpers] Error in updateOrderTrackingRecords:', error);
        throw error;
    }
};

const calculateNewItemsForTracking = (originalProducts, updatedProducts) => {
    const newItems = [];

    updatedProducts.forEach(updatedProduct => {
        const originalProduct = originalProducts.find(p => {
            if (p.product_code !== updatedProduct.product_code) return false;

            const originalAddons = (p.add_ons || []).map(addon => addon.product_code || addon.id).sort();
            const updatedAddons = (updatedProduct.add_ons || []).map(addon => addon.product_code || addon.id).sort();

            if (originalAddons.length !== updatedAddons.length) return false;
            return originalAddons.every((id, index) => id === updatedAddons[index]);
        });

        if (!originalProduct) {
            newItems.push({ ...updatedProduct });
        } else if (updatedProduct.quantity > originalProduct.quantity) {
            const additionalQuantity = updatedProduct.quantity - originalProduct.quantity;
            newItems.push({
                ...updatedProduct,
                quantity: additionalQuantity
            });
        }
    });

    return newItems;
};

// Keep the shelf in step when a HELD order GAINS items.
//
// A pending order reserves its stock the moment it is held, so that the
// kitchen cannot start cooking something the shelf has already promised
// elsewhere. Appending to that order — the waiter's "add to table", the
// cashier's UPDATE ORDER — never deducted the items it added. Nothing
// noticed, because POST /transactions decremented the WHOLE bill again at
// settle time: the original items came off twice and the added ones once,
// which read as "roughly right" per bill and was in fact wrong on both counts.
//
// Now that a settled pending order tells the server its stock is already
// gone (see TransactionCreate.stock_already_deducted), the settle-time
// decrement no longer covers for this — added items would come off ZERO
// times and quietly oversell. So the deduction has to happen here, where the
// items are actually added.
//
// Reuses the two existing helpers rather than reimplementing either: the
// same differ updateOrderTrackingRecords uses to decide what the kitchen has
// not seen yet, and the same deduction the hold itself performs (which
// re-reads current stock per product rather than trusting a cached
// catalogue).
//
// REMOVING items from a held order still leaves their stock deducted. That
// is pre-existing and deliberately untouched here — it errs towards holding
// stock back rather than overselling it, which is the safe direction.
const deductStockForAddedItems = async (originalProducts, updatedProducts, selectedDiningOption, tableNumber, cashierName) => {
    const addedItems = calculateNewItemsForTracking(originalProducts || [], updatedProducts || []);
    if (addedItems.length === 0) return;
    await deductStockForPendingOrder(addedItems, selectedDiningOption, tableNumber, cashierName);
};

const deductInventoryForAdditionalItems = async (newItemsToTrack, selectedDiningOption, editingOrderKey, username, products) => {
    try {
        for (const product of newItemsToTrack) {
            if (product.stock_calculation_type === 'auto') {
                await api.post('/inventory/deduct-for-pending-order', {
                    product_code: product.product_code,
                    quantity: product.quantity,
                    dining_option: selectedDiningOption,
                    table_key: editingOrderKey,
                    created_by: `Order Update - ${username}`,
                    notes: `Additional items for order update: ${product.name}`
                });
                console.log(` Deducted ${product.quantity}x ${product.name} from inventory`);
            } else {
                if (product.quantity !== -9999) {
                    const currentProduct = products.find(p => p.product_code === product.product_code);
                    if (currentProduct) {
                        const newQuantity = Math.max(0, currentProduct.quantity - product.quantity);
                        await productService.updateProduct(product.product_code, {
                            ...currentProduct,
                            quantity: newQuantity
                        });
                        console.log(` Deducted ${product.quantity}x ${product.name} from manual stock`);
                    }
                }
            }
        }
    } catch (error) {
        console.error('Error deducting inventory for additional items:', error);
        throw error;
    }
};

export {
    deductStockForPendingOrder,
    revertStockDeduction,
    createOrderTracking,
    updateOrderTrackingRecords,
    calculateNewItemsForTracking,
    deductStockForAddedItems,
    deductInventoryForAdditionalItems
};